import React, { useRef, useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { useToggle } from '@/src/hooks';
import { useKey } from 'react-use';
import { useStask } from '@/src/hooks/useStask';
import { BitMap, Color } from './bitmap';
import { PixelBoard } from './PixelBoard';
import { BW2x2, CURSOR_PANCEL32x32_DATA_URL, CURSOR_BUCKET32x32_DATA_URL } from '../bins';
import { bitMap2Image, base642BitMap, bitMap2ImageData } from './utils';
import { DrawMode, DrawTool } from './types';

export interface CanvasProps {
  onBitMapChange?: (bitMap: BitMap<Color>) => void;
  bitMap: BitMap<Color>;
  /**
   * 像素块大小
   */
  pixelSize: number;
  drawMode?: DrawMode;
  preview?: boolean;
  color?: Color;
  onOperationFinish?: (bitMap: BitMap<Color>) => void;
  onMouseMove?: (xy: [number, number]) => void;
  grid?: boolean;
  tool?: DrawTool;
}

export function Canvas(props: CanvasProps) {
  const {
    pixelSize,
    onOperationFinish,
    bitMap,
    onBitMapChange,
    onMouseMove,
  } = props;
  const { width, height } = useMemo(() => ({ width: bitMap.width, height: bitMap.height }), [bitMap]);
  const drawMode = props.drawMode ?? DrawMode.normal;
  const preview = props.preview ?? false;
  const grid = props.grid ?? false;
  const tool = props.tool ?? DrawTool.pancel;
  const color = props.color || Color.black;
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [isDown, toggleDU] = useToggle(false);
  const backgroundImageURL = useMemo(() => bitMap2Image(base642BitMap(BW2x2), pixelSize).toDataURL(),
    [pixelSize, BW2x2])

  useEffect(() => {
    const { current } = canvas;
    if (current) {
      const ctx = current.getContext('2d');
      if (ctx) {
        ctx.putImageData(bitMap2ImageData(bitMap, pixelSize), 0, 0);
      }
    }
  }, [canvas, bitMap, pixelSize]);



  const fillRect = useMemo(() => (bitMap: BitMap<Color>, _x: number, _y: number) => {
    if (bitMap.get(_x, _y).eq(color)) {
      return bitMap;
    }
    return bitMap.clone().put(_x, _y, color);
  }, [color]);

  const pencelDraw = useMemo(() => (bx: number, by: number) => {
    const { width, height } = bitMap;
    // 当前点
    let b = fillRect(bitMap, bx, by);
    if (drawMode === DrawMode.lrs) {
      // 左右对称点
      b = fillRect(b, width - (bx + 1), by);
    }
    if (drawMode === DrawMode.uds) {
      b = fillRect(b, bx, height - (by + 1));
    }
    if (drawMode === DrawMode.centrosymmetry) {
      b = fillRect(b, width - (bx + 1), height - (by + 1));
    }
    // 提交变更
    if (!b.eq(bitMap) && onBitMapChange) {
      onBitMapChange(b);
    }
  }, [fillRect, pixelSize, onBitMapChange, drawMode, bitMap]);

  const bucketDraw = useMemo(() => (bx: number, by: number) => {
    let b = bitMap.fillArea(bx, by, color);
    // 提交变更
    if (onBitMapChange)
      onBitMapChange(b);
  }, [fillRect, pixelSize, onBitMapChange, bitMap, color]);

  let draw = pencelDraw;

  switch (tool) {
    case DrawTool.bucket: draw = bucketDraw; break;
    default: draw = pencelDraw;
  }

  const handleMouseDown = useMemo(() => (x: number, y: number) => {
    toggleDU(true);
    // 把绘图前的状态存一下
    if (onOperationFinish) {
      onOperationFinish(bitMap.clone());
    }
    draw(x, y);
  }, [toggleDU, draw, bitMap, onOperationFinish]);

  const handleMouseUp = useMemo(() => () => {
    toggleDU(false);
  }, [])

  const handleMouseMove = useMemo(() => (x: number, y: number) => {
    if (isDown) {
      draw(x, y);
    }
    if (onMouseMove) {
      onMouseMove([x, y]);
    }
  }, [isDown, draw]);

  const {
    Container,
    DrawCanvas,
  } = S;
  return (
    <Container
      style={{
        cursor: tool === DrawTool.pancel ? `url(${CURSOR_PANCEL32x32_DATA_URL}),default` : `url(${CURSOR_BUCKET32x32_DATA_URL}),default`,
      }}
    >
      <PixelBoard
        {...{ width, height, pixelSize, grid }}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <DrawCanvas
          ref={canvas}
          style={{
            backgroundImage: grid ? 'none' : `url(${backgroundImageURL})`
          }}
          {...{
            width: width * pixelSize,
            height: height * pixelSize
          }}
        />
      </PixelBoard>
    </Container>
  );
}

export const useCanvas = (width: number, height: number, pixelSize: number): CanvasProps => {
  const [bitMap, onBitMapChange] = useState<BitMap<Color>>(new BitMap(width, height, Color.none));
  // change bitMap的宽高时的变化
  useEffect(() => {
    if (width !== bitMap.width || height !== bitMap.height) {
      const newBitMap = bitMap.updateSize(Color.none, width, height);
      onBitMapChange(newBitMap);
    }
  }, [width, height])
  return {
    pixelSize,
    color: Color.black,
    bitMap,
    onBitMapChange,
  }
}

const S = {
  Container: styled.div`
    display: inline-block;
  `,
  DrawCanvas: styled.canvas``,
};

export function useUndo({
  onBitMapChange,
}: {
  onBitMapChange?: (bitMap: BitMap<Color>) => void;
}) {
  const [top, length, { pop, push }] = useStask();

  useKey(
    event => event.key === 'z' && event.metaKey === true,
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (top && onBitMapChange) {
        onBitMapChange(top as any);
      }
      if (length > 0) {
        pop();
      }
    }, { event: 'keydown' });
  const handleOperationFinish = (bit: BitMap<Color>) => {
    push(bit.clone());
  }
  return handleOperationFinish;
}

